import {
  CustomLayoutDrawerContent,
  DrawerBreadcrumb,
  DrawerBreadCrumbs,
} from 'module/ui/components/atoms/drawer';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { Label01 } from 'module/ui/components/styles';
import { faFile } from '@fortawesome/pro-duotone-svg-icons';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Button, Icon } from '@rocketlane/ui';
import { Upload } from 'antd';
import Dragger from 'antd/lib/upload/Dragger';
import { useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { getIconForFileType } from '../file-viewer/icon-for-file-type';

export type UploadFile = {
  lastModified: number;
  lastModifiedDate: Date;
  name: string;
  originFileObj: File;
  percent: number;
  size: number;
  type: string;
  uid: string;
};
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 310px;
  overflow: hidden;
  > span {
    flex: 1;
    height: 100%;
    display: flex;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-05);
`;
const EmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: var(--spacing-02);
  ${Label01};
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-02);
  border: 1px solid var(--divider-subtle);
  border-radius: var(--border-radius-small);
  padding: var(--spacing-05);
`;
const FileContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-05);
  .rl-icon {
    flex-shrink: 0;
  }
`;
const FileNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
`;
const FileName = styled.span`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semi-bold);
  line-height: var(--line-height-02);
  letter-spacing: var(--letter-spacing-01);
`;
const FileType = styled.span`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-02);
  letter-spacing: var(--letter-spacing-01);
  color: var(--black-70);
`;

export type AddFilesPayload = {
  files: UploadFile[];
};
type Props = {
  openFileDialog?: boolean;
  onDone?: (payload: AddFilesPayload) => Promise<void>;
  onClose: () => void;
  breadcrumbs?: DrawerBreadcrumb[];
};
export const AddFiles = ({ onDone, onClose, breadcrumbs }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const onFileChange = useCallback((info: any) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setSelectedFiles(info.fileList);
    }, 100);
  }, []);

  const handleAddToKnowledgeBase = useCallback(async () => {
    if (onDone) {
      setActionLoading(true);
      await onDone({ files: selectedFiles });
      setActionLoading(false);
    }
  }, [onDone, selectedFiles]);

  return (
    <CustomLayoutDrawerContent>
      <CustomLayoutDrawerContent.Title
        breadcrumbs={
          breadcrumbs ? (
            <DrawerBreadCrumbs breadcrumbs={breadcrumbs} />
          ) : undefined
        }
      >
        <FormattedMessage id="knowledge_base.add_files.title" />
      </CustomLayoutDrawerContent.Title>
      <CustomLayoutDrawerContent.Content>
        <Wrapper>
          {selectedFiles.length > 0 ? (
            <div>
              {selectedFiles.map((file) => (
                <FileItem key={file.uid}>
                  <FileContent>
                    <FontAwesomeIcon
                      icon={getIconForFileType(file.name)}
                      size={12}
                    />
                    <FileNameWrapper>
                      <FileName>{file.name}</FileName>
                      <FileType>{file.type}</FileType>
                    </FileNameWrapper>
                  </FileContent>
                  <Button
                    variant="ghost"
                    size="medium"
                    leftIcon={<FontAwesomeIcon icon={faXmark} size={12} />}
                    onClick={() => {
                      setSelectedFiles(
                        selectedFiles.filter((f) => f.uid !== file.uid),
                      );
                    }}
                  />
                </FileItem>
              ))}
            </div>
          ) : (
            <Dragger
              accept=".pdf,.md,.txt,.docx,.doc"
              onChange={onFileChange}
              multiple
              openFileDialogOnClick={!selectedFiles.length}
              showUploadList={false}
            >
              <EmptyState>
                <EmptyStateContent>
                  <Icon icon={faFile} size="3x" color="var(--blue-30)" />
                  <FormattedMessage id="knowledge_base.add_files.empty.title" />
                </EmptyStateContent>
                <Button variant="primary">
                  <CenterAlignedBox>
                    <Icon icon={faPlus} size=".75x" />
                    <FormattedMessage id="knowledge_base.add_files.button.label" />
                  </CenterAlignedBox>
                </Button>
              </EmptyState>
            </Dragger>
          )}
        </Wrapper>
        <Upload
          accept=".pdf,.md,.txt,.docx,.doc"
          multiple
          showUploadList={false}
          beforeUpload={() => {
            return false;
          }}
          onChange={onFileChange}
          fileList={[]}
        >
          <div ref={inputRef}></div>
        </Upload>
      </CustomLayoutDrawerContent.Content>
      <CustomLayoutDrawerContent.Footer $rightAlignedAction>
        <Button variant="secondary" onClick={onClose}>
          <FormattedMessage id="cancel" />
        </Button>
        <Button
          variant="primary"
          onClick={handleAddToKnowledgeBase}
          disabled={selectedFiles.length === 0 || actionLoading}
          loading={actionLoading}
        >
          <FormattedMessage id="knowledge_base.drawer.actions.add_to_knowledge_base" />
        </Button>
      </CustomLayoutDrawerContent.Footer>
    </CustomLayoutDrawerContent>
  );
};
